import { useLastPrivateAgent } from "../hooks/useLastPrivateAgent";
import {
  AgentAvatar,
  Banner,
  Button,
  EmptyState,
  Field,
  Select,
  Spinner
} from "@salesko/design-system";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Monitor, Save } from "lucide-react";
import { useEffect, useState } from "react";
import type {
  AgentResearchAgentProfile,
  PersonalAgentDevice,
  PrivateAgentProfile,
  PrivateAgentPlacement,
  PrivateAgentProfileWrite,
  PrivateAgentRuntimePreference
} from "@salesko/contracts";
import type { PrivateAgentProfileProjectionSyncStatePhase3 } from "@salesko/contracts/private-agent-profile-projection";
import { useI18n, type Dictionary } from "../../i18n";
import {
  AgentResearchApiError,
  createPrivateAgent,
  deletePrivateAgentProfile,
  listPrivateAgents,
  listPersonalAgentDevices,
  savePrivateAgentProfile
} from "../../lib/api";
import { SettingsNote, SettingsSection } from "./SettingsSection";
import { PrivateAgentSelector, privateAgentSelectionCopy } from "../components/PrivateAgentSelector";

import { ProviderProfileBindingSchema, type ProviderProfileBinding } from "@byok-sdk/protocol";

const runtimePreferences: PrivateAgentRuntimePreference[] = [
  "claude_subscription",
  "codex_subscription",
  "pi_provider_key"
];
const researchPresets: AgentResearchAgentProfile[] = [
  "relationship_research",
  "relationship_research_cn",
  "relationship_research_tw",
  "relationship_research_hk"
];

export type PrivateAgentDraft = {
  deviceId: string;
  name: string;
  providerBindingJson: string;
  subscriptionModelId?: string;
  researchPreset: string;
  runtimePreference: PrivateAgentRuntimePreference;
};

const emptyDraft: PrivateAgentDraft = {
  deviceId: "",
  name: "",
  providerBindingJson: "",
  researchPreset: "relationship_research",
  runtimePreference: "claude_subscription"
};

/**
 * Agent collection with explicit editing selection and initial Placement.
 *
 * Device truth has exactly one source — `GET /api/agent/devices` — so this
 * panel composes the profile with that list rather than asking the API to
 * annotate the profile. A binding whose computer is gone or revoked is shown
 * as "needs re-binding" and nothing is rewritten behind the user's back.
 * No credential field exists here by design: runtime sign-in stays on the
 * computer.
 */
export function PrivateAgentPanel({ enabled }: { enabled: boolean }) {
  const navigate = useNavigate();
  return <PrivateAgentCollection enabled={enabled} onOpenComputers={() => void navigate({ to: "/settings/$section", params: { section: "computers" } })} />;
}

export function PrivateAgentCollection({ enabled, onOpenComputers }: { enabled: boolean; onOpenComputers: () => void }) {
  const queryClient = useQueryClient();
  const { locale, t } = useI18n();
  const copy = privateAgentSelectionCopy[locale];
  const [lastAgentId, setSelectedAgentId] = useLastPrivateAgent();
  const [creating, setCreating] = useState(false);
  const selectedAgentId = creating ? undefined : lastAgentId;

  const profileQuery = useQuery({
    queryKey: ["private-agents"],
    queryFn: listPrivateAgents,
    enabled,
    retry: false,
    staleTime: 30_000,
    refetchInterval: (query) => query.state.data?.agents.some((agent) => agent.projectionSync.status === "pending") ? 2_000 : false
  });
  const devicesQuery = useQuery({
    queryKey: ["personal-agent-devices"],
    queryFn: listPersonalAgentDevices,
    enabled,
    retry: 1,
    staleTime: 15_000
  });

  const saveMutation = useMutation({
    mutationFn: ({ agentId, write, deviceId }: { agentId: string | undefined; write: PrivateAgentProfileWrite; deviceId: string }) =>
      agentId ? savePrivateAgentProfile(agentId, write) : createPrivateAgent({ profile: write, placement: { deviceId } }),
    onSuccess: (response) => {
      queryClient.setQueryData(["private-agent-profile", response.profile.agentId], response);
      void queryClient.invalidateQueries({ queryKey: ["private-agents"] });
      void queryClient.invalidateQueries({ queryKey: ["private-agent-readiness", response.profile.agentId] });
      setSelectedAgentId(response.profile.agentId);
      setCreating(false);
    }
  });
  const deleteMutation = useMutation({
    mutationFn: deletePrivateAgentProfile,
    onSuccess: (_, agentId) => {
      queryClient.removeQueries({ queryKey: ["private-agent-profile", agentId] });
      queryClient.removeQueries({ queryKey: ["private-agent-readiness", agentId] });
      void queryClient.invalidateQueries({ queryKey: ["private-agents"] });
      setSelectedAgentId(undefined);
      setCreating(false);
    }
  });

  const agents = enabled ? profileQuery.data?.agents ?? [] : [];
  const selected = agents.find((agent) => agent.profile.agentId === selectedAgentId);
  const pending = saveMutation.isPending || deleteMutation.isPending;
  return <>
    {enabled ? <SettingsSection ariaLabel={t.privateAgent.ariaLabel} id="settings-private-agent-collection" title={t.privateAgent.title}>
      <PrivateAgentSelector
        agents={agents.map((agent) => agent.profile)}
        agentId={selectedAgentId}
        disabled={pending || profileQuery.isLoading}
        onChange={(agentId) => { setSelectedAgentId(agentId); setCreating(false); saveMutation.reset(); deleteMutation.reset(); }}
      />
      <Button disabled={pending || profileQuery.isError} onClick={() => {
        setCreating(true); saveMutation.reset(); deleteMutation.reset();
      }}>{copy.create}</Button>
      {profileQuery.isLoading ? <Spinner /> : null}
      {profileQuery.isError ? <Banner status="error" action={<Button onClick={() => void profileQuery.refetch()}>{t.common.retry}</Button>}>{copy.loadError}</Banner> : null}
      {deleteMutation.isError ? <Banner status="error">{saveErrorMessage(deleteMutation.error, t)}</Banner> : null}
      {!creating && !selected ? <SettingsNote>{selectedAgentId ? copy.unavailable : copy.choose}</SettingsNote> : null}
    </SettingsSection> : null}
    {(!enabled || creating || selected) ? (
    <PrivateAgentPanelView
      key={selectedAgentId ?? "create"}
      deletePending={deleteMutation.isPending}
      devices={devicesQuery.data?.devices ?? []}
      devicesLoading={devicesQuery.isFetching}
      enabled={enabled}
      onDelete={() => { if (selectedAgentId) deleteMutation.mutate(selectedAgentId); }}
      onDraftChange={() => { if (!pending) saveMutation.reset(); }}
      onOpenComputers={onOpenComputers}
      onSave={(write, deviceId) => saveMutation.mutate({ agentId: selectedAgentId, write, deviceId })}
      profile={selected?.profile ?? null}
      placement={selected?.placement ?? null}
      projectionSync={selected?.projectionSync ?? null}
      profileLoadError={profileQuery.isError ? profileQuery.error : null}
      profileLoading={profileQuery.isLoading}
      saveError={saveMutation.isError ? saveMutation.error : null}
      savePending={pending}
      saveSucceeded={saveMutation.isSuccess}
    />
    ) : null}
  </>;
}

export type PrivateAgentPanelViewProps = {
  deletePending: boolean;
  devices: PersonalAgentDevice[];
  devicesLoading: boolean;
  enabled: boolean;
  onDelete: () => void;
  /** Called on every draft edit; the container uses it to drop a stale save confirmation. */
  onDraftChange: () => void;
  onOpenComputers: () => void;
  onSave: (write: PrivateAgentProfileWrite, deviceId: string) => void;
  profile: PrivateAgentProfile | null;
  placement: PrivateAgentPlacement | null;
  projectionSync: PrivateAgentProfileProjectionSyncStatePhase3 | null;
  profileLoadError: unknown;
  profileLoading: boolean;
  saveError: unknown;
  savePending: boolean;
  saveSucceeded: boolean;
};

/** Presentation only, so every state can be rendered directly in tests. */
export function PrivateAgentPanelView({
  deletePending,
  devices,
  devicesLoading,
  enabled,
  onDelete,
  onDraftChange,
  onOpenComputers,
  onSave,
  profile,
  placement,
  projectionSync,
  profileLoadError,
  profileLoading,
  saveError,
  savePending,
  saveSucceeded
}: PrivateAgentPanelViewProps) {
  const { t, locale } = useI18n();
  const copy = privateAgentSelectionCopy[locale];
  const [draft, setDraft] = useState<PrivateAgentDraft>(() => profile ? draftFromProfile(profile, placement) : emptyDraft);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [specifyModel, setSpecifyModel] = useState(Boolean(profile?.subscriptionModelId));
  const providerProfile = parseProviderBinding(draft.providerBindingJson);
  const bindingCopy = providerBindingCopy[locale];
  // One edit path for every control, so no field can change the draft without
  // also invalidating the "saved" confirmation the previous write left behind.
  const editDraft = createDraftEditor(setDraft, onDraftChange);

  const activeDevices = devices.filter((device) => !device.revoked);
  // Device truth has one source. If the bound computer is not in the active
  // list, say so and let the owner re-bind — never silently repoint the agent.
  const boundDeviceUnusable = Boolean(
    profile && !activeDevices.some((device) => device.deviceId === placement?.deviceId)
  );

  useEffect(() => {
    if (!profile) {
      return;
    }
    setDraft(draftFromProfile(profile, placement));
    setSpecifyModel(Boolean(profile.subscriptionModelId));
  }, [profile, placement]);

  const saveDisabled =
    !enabled ||
    savePending ||
    profileLoading || Boolean(profileLoadError) ||
    (!profile && !activeDevices.some((device) => device.deviceId === draft.deviceId)) ||
    draft.name.trim().length === 0 ||
    (draft.runtimePreference !== "pi_provider_key" && specifyModel && !draft.subscriptionModelId?.trim()) ||
    (profile !== null && placement === null) ||
    (draft.runtimePreference === "pi_provider_key" &&
      providerProfile === undefined);

  return (
    <SettingsSection
      ariaLabel={t.privateAgent.ariaLabel}
      id="settings-private-agent"
      title={t.privateAgent.title}
      trailing={
        profileLoading || devicesLoading ? <Spinner size={16} /> : <AgentAvatar size="xs" variant="private" />
      }
    >
      {!enabled ? <SettingsNote>{t.privateAgent.signIn}</SettingsNote> : null}

      {enabled ? (
        <>
          <SettingsNote tone="body">{t.privateAgent.description}</SettingsNote>

          {profileLoading ? <Spinner label={t.privateAgent.loading} size={16} /> : null}
          {profileLoadError ? <Banner status="error" title={t.privateAgent.loadError} /> : null}

          {boundDeviceUnusable ? (
            <Banner status="warning" title={t.privateAgent.needsRebindingTitle}>
              {copy.fixedComputer}
            </Banner>
          ) : null}

          {!profile && activeDevices.length === 0 ? (
            <EmptyState
              action={
                <Button onClick={onOpenComputers} size="sm" variant="secondary">
                  {t.privateAgent.goToComputers}
                </Button>
              }
              icon={<Monitor size={20} />}
              title={t.privateAgent.noComputersTitle}
            >
              {t.privateAgent.noComputers}
            </EmptyState>
          ) : (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                if (saveDisabled) {
                  return;
                }
                onSave({
                  name: draft.name.trim(),
                  ...(draft.runtimePreference === "pi_provider_key"
                    ? { providerProfile }
                    : specifyModel ? { subscriptionModelId: draft.subscriptionModelId?.trim() } : {}),
                  researchPreset: draft.researchPreset,
                  runtimePreference: draft.runtimePreference
                }, draft.deviceId);
              }}
              style={{ display: "grid", gap: "var(--space-4)" }}
            >
              {profile ? <SettingsNote>{t.privateAgent.computer}: {devices.find((device) => device.deviceId === placement?.deviceId)?.deviceName ?? t.privateAgent.needsRebindingTitle}. {copy.fixedComputer}</SettingsNote> : <Select
                hint={t.privateAgent.computerHint}
                label={t.privateAgent.computer}
                placeholder={copy.computer}
                onChange={(value) => editDraft({ deviceId: value })}
                options={activeDevices.map((device) => ({ label: device.deviceName, value: device.deviceId }))}
                value={draft.deviceId}
              />}

              <Field
                label={t.privateAgent.name}
                maxLength={80}
                onChange={(value) => editDraft({ name: value })}
                placeholder={t.privateAgent.namePlaceholder}
                value={draft.name}
              />

              {profile ? <SettingsNote>{t.privateAgent.runtime}: {runtimeLabel(profile.runtimePreference, t)}</SettingsNote> : <Select
                hint={t.privateAgent.runtimeHint}
                label={t.privateAgent.runtime}
                onChange={(value) => {
                  const next = runtimePreferences.find((preference) => preference === value);
                  if (next) {
                    editDraft({ runtimePreference: next, subscriptionModelId: "" });
                    setSpecifyModel(false);
                  }
                }}
                options={runtimePreferences.map((preference) => ({
                  label: runtimeLabel(preference, t),
                  value: preference
                }))}
                value={draft.runtimePreference}
              />}

              {draft.runtimePreference === "pi_provider_key" ? (
                <>
                  <SettingsNote>{bindingCopy.hint} <code>salesko-agent keys binding &lt;profile-ref&gt;</code></SettingsNote>
                  <Field label={bindingCopy.label} value={draft.providerBindingJson} maxLength={4096}
                    onChange={(value) => editDraft({ providerBindingJson: value })} />
                  {providerProfile ? <SettingsNote>{providerProfile.profileRef} · {providerProfile.modelId} · {providerProfile.profileRevision}</SettingsNote>
                    : draft.providerBindingJson ? <SettingsNote>{bindingCopy.invalid}</SettingsNote> : null}
                </>
              ) : (
                <>
                  <Select
                    label={t.privateAgent.subscriptionModel}
                    value={specifyModel ? "explicit" : "default"}
                    options={[
                      { value: "default", label: t.privateAgent.runtimeDefaultModel },
                      { value: "explicit", label: t.privateAgent.specifyModel }
                    ]}
                    onChange={(value) => {
                      setSpecifyModel(value === "explicit");
                      editDraft({ subscriptionModelId: "" });
                    }}
                  />
                  {specifyModel ? <Field
                    label={t.privateAgent.modelId}
                    hint={t.privateAgent.subscriptionModelHint}
                    maxLength={160}
                    value={draft.subscriptionModelId ?? ""}
                    onChange={(value) => editDraft({ subscriptionModelId: value })}
                  /> : null}
                </>
              )}

              <Select
                hint={t.privateAgent.presetHint}
                label={t.privateAgent.researchPreset}
                onChange={(value) => editDraft({ researchPreset: value })}
                options={researchPresets.map((preset) => ({ label: presetLabel(preset, t), value: preset }))}
                value={draft.researchPreset}
              />

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <Button disabled={saveDisabled} iconLeft={<Save size={15} />} size="sm" type="submit">
                  {t.privateAgent.save}
                </Button>
                {profile ? (
                  <Button
                    disabled={deletePending}
                    onClick={() => setConfirmingDelete(true)}
                    size="sm"
                    variant="ghost"
                  >
                    {t.privateAgent.remove}
                  </Button>
                ) : null}
              </div>

              {confirmingDelete ? (
                <Banner
                  action={
                    <div style={{ display: "flex", gap: 8 }}>
                      <Button
                        disabled={deletePending}
                        onClick={() => {
                          setConfirmingDelete(false);
                          editDraft(emptyDraft);
                          onDelete();
                        }}
                        size="sm"
                        variant="danger"
                      >
                        {t.privateAgent.removeConfirmAction}
                      </Button>
                      <Button onClick={() => setConfirmingDelete(false)} size="sm" variant="ghost">
                        {t.privateAgent.removeCancel}
                      </Button>
                    </div>
                  }
                  status="warning"
                >
                  {t.privateAgent.removeConfirm}
                </Banner>
              ) : null}

              {saveSucceeded ? <Banner status="success">{t.privateAgent.saved}</Banner> : null}
              {projectionSync?.status === "pending" ? <Banner status="warning">{t.privateAgent.projectionPending}</Banner> : null}
              {projectionSync?.status === "synced" ? <Banner status="success">{t.privateAgent.projectionSynced}</Banner> : null}
              {projectionSync?.status === "failed" ? (
                <Banner status="error">
                  {t.privateAgent.projectionFailed}
                  {projectionSync.reasonCode ? ` (${projectionSync.reasonCode})` : ""}
                </Banner>
              ) : null}
              {saveError ? <Banner status="error">{saveErrorMessage(saveError, t)}</Banner> : null}
            </form>
          )}
        </>
      ) : null}
    </SettingsSection>
  );
}

/**
 * Builds the single draft-edit entrypoint. Editing the draft makes the previous
 * write's confirmation stale, so every edit both applies the patch and signals
 * the container to reset the save mutation — the banner is derived from that
 * mutation, so it has exactly one source.
 */
export function createDraftEditor(
  setDraft: (update: (current: PrivateAgentDraft) => PrivateAgentDraft) => void,
  onDraftChange: () => void
) {
  return (patch: Partial<PrivateAgentDraft>) => {
    onDraftChange();
    setDraft((current) => ({ ...current, ...patch }));
  };
}

export function draftFromProfile(profile: PrivateAgentProfile, placement: PrivateAgentPlacement | null): PrivateAgentDraft {
  return {
    deviceId: placement?.deviceId ?? "",
    name: profile.name,
    providerBindingJson: profile.providerProfile === undefined ? "" : JSON.stringify(profile.providerProfile),
    subscriptionModelId: profile.subscriptionModelId ?? "",
    researchPreset: profile.researchPreset,
    runtimePreference: profile.runtimePreference
  };
}

/** 409 from the write path is a specific, actionable state; anything else is generic. */
export function saveErrorMessage(error: unknown, t: Dictionary) {
  if (error instanceof AgentResearchApiError && error.code === "private_agent_device_not_active") {
    return t.privateAgent.deviceNotActive;
  }
  return t.privateAgent.saveError;
}

function runtimeLabel(preference: PrivateAgentRuntimePreference, t: Dictionary) {
  switch (preference) {
    case "claude_subscription":
      return t.privateAgent.runtimes.claudeSubscription;
    case "codex_subscription":
      return t.privateAgent.runtimes.codexSubscription;
    case "pi_provider_key":
      return t.privateAgent.runtimes.piProviderKey;
  }
}

function presetLabel(preset: string, t: Dictionary) {
  switch (preset) {
    case "relationship_research_cn":
      return t.privateAgent.profiles.mainland;
    case "relationship_research_tw":
      return t.privateAgent.profiles.taiwan;
    case "relationship_research_hk":
      return t.privateAgent.profiles.hongKong;
    default:
      return t.privateAgent.profiles.default;
  }
}

/** Parse only the selected device's exported non-secret binding; never infer missing fields. */
export function parseProviderBinding(input: string): ProviderProfileBinding | undefined {
  if (new TextEncoder().encode(input).byteLength > 4096) return undefined;
  try {
    const parsed = ProviderProfileBindingSchema.safeParse(JSON.parse(input));
    return parsed.success ? parsed.data : undefined;
  } catch { return undefined; }
}

const providerBindingCopy = {
  en: { label: "Local model binding", hint: "Configure Pi on the selected computer, then paste its binding export. This contains no API key:", invalid: "The binding is incomplete or invalid. Export it again from the selected computer." },
  "zh-Hans": { label: "本地模型绑定", hint: "在所选电脑配置 Pi 后，粘贴以下命令导出的绑定（不含 API key）：", invalid: "绑定不完整或格式无效，请从所选电脑重新导出。" },
  "zh-Hant": { label: "本地模型綁定", hint: "在所選電腦設定 Pi 後，貼上以下指令匯出的綁定（不含 API key）：", invalid: "綁定不完整或格式無效，請從所選電腦重新匯出。" },
  ja: { label: "ローカルモデルのバインディング", hint: "選択した端末で Pi を設定し、次のコマンドの出力を貼り付けます。API キーは含まれません：", invalid: "バインディングが不完全または無効です。選択した端末から再度エクスポートしてください。" },
};
